<script setup>
import { TransitionGroup } from 'vue'
import { layoutConfig } from '@layouts'
import { TransitionExpand, VerticalNavLink } from '@layouts/components'
import { canViewNavMenuGroup } from '@layouts/plugins/casl'
import { useLayoutConfigStore } from '@layouts/stores/config'
import { injectionKeyIsVerticalNavHovered } from '@layouts/symbols'
import {
    getDynamicI18nProps,
    isNavGroupActive,
    openGroups,
} from '@layouts/utils'

const props = defineProps({
    item: {
        type: null,
        required: true,
    },
})

defineOptions({
    name: 'VerticalNavGroup',
})

const route = useRoute()
const router = useRouter()
const configStore = useLayoutConfigStore()
// Always show text labels
const hideTitleAndBadge = ref(false)

// Always keep navigation expanded
const isVerticalNavHovered = ref(true)

const isGroupActive = ref(true)
const isGroupOpen = ref(false)

const isAnyChildOpen = (children) => {
    return children.some((child) => {
        let result = openGroups.value.includes(child.title)
        if ('children' in child)
            result = isAnyChildOpen(child.children) || result

        return result
    })
}

const collapseChildren = (children) => {
    children.forEach((child) => {
        if ('children' in child) collapseChildren(child.children)
        openGroups.value = openGroups.value.filter(
            (group) => group !== child.title
        )
    })
}

/*Watch for route changes, more specifically route path. Do note that this won't trigger if route's query is updated.

updates isActive & isOpen based on active state of group.
*/
watch(
    () => route.path,
    () => {
        const isActive = isNavGroupActive(props.item.children, router)

        // Only open the group if it contains the active route; don't touch it otherwise
        if (isActive) isGroupOpen.value = true
        isGroupActive.value = isActive
    },
    { immediate: true }
)
watch(
    isGroupOpen,
    (val) => {
        // Find group index for adding/removing group from openGroups array
        const grpIndex = openGroups.value.indexOf(props.item.title)

        // update openGroups array for addition/removal of current group

        // If group is opened => Add it to `openGroups` array
        if (val && grpIndex === -1) {
            openGroups.value.push(props.item.title)
        } else if (!val && grpIndex !== -1) {
            openGroups.value.splice(grpIndex, 1)
            collapseChildren(props.item.children)
        }
    },
    { immediate: true }
)

// Always keep groups open - accordion auto-close disabled
// (openGroups watcher removed to prevent inactive groups from collapsing)

// Always keep groups open - disable mini mode watcher
// watch(configStore.isVerticalNavMini(isVerticalNavHovered), (val) => {
//     isGroupOpen.value = val ? false : isGroupActive.value
// })
</script>

<template>
    <li
        class="nav-group"
        :class="[
            {
                active: isGroupActive,
                open: isGroupOpen,
                disabled: item.disable,
            },
        ]"
    >
        <div class="nav-group-label" @click="isGroupOpen = !isGroupOpen">
            <Component
                :is="layoutConfig.app.iconRenderer || 'div'"
                v-bind="
                    item.icon ||
                    layoutConfig.verticalNav.defaultNavItemIconProps
                "
                class="nav-item-icon"
            />

            <Component :is="TransitionGroup" name="transition-slide-x">
                <!-- 👉 Title -->
                <Component
                    :is="layoutConfig.app.i18n.enable ? 'i18n-t' : 'span'"
                    v-bind="getDynamicI18nProps(item.title, 'span')"
                    v-show="!hideTitleAndBadge"
                    key="title"
                    class="nav-item-title"
                >
                    {{ item.title }}
                </Component>

                <!-- 👉 Badge -->
                <Component
                    :is="
                        layoutConfig.app.i18n.enable &&
                        isNaN(Number(item.badgeContent))
                            ? 'i18n-t'
                            : 'span'
                    "
                    v-bind="getDynamicI18nProps(item.badgeContent, 'span')"
                    v-show="!hideTitleAndBadge"
                    v-if="item.badgeContent"
                    key="badge"
                    class="nav-item-badge"
                    :class="item.badgeClass"
                >
                    {{ item.badgeContent }}
                </Component>
                <Component
                    :is="layoutConfig.app.iconRenderer || 'div'"
                    v-show="!hideTitleAndBadge"
                    v-bind="layoutConfig.icons.chevronRight"
                    key="arrow"
                    class="nav-group-arrow"
                />
            </Component>
        </div>
        <TransitionExpand>
            <ul v-show="isGroupOpen" class="nav-group-children">
                <Component
                    :is="
                        'children' in child
                            ? 'VerticalNavGroup'
                            : VerticalNavLink
                    "
                    v-for="child in item.children"
                    :key="child.title"
                    :item="child"
                />
            </ul>
        </TransitionExpand>
    </li>
</template>

<style lang="scss">
.layout-vertical-nav {
    .nav-group {
        &-label {
            display: flex;
            align-items: center;
            cursor: pointer;
        }
    }
}
</style>
